import { Crisp } from 'crisp-sdk-web';
import { useEffect, useLayoutEffect } from 'react';
import { createGlobalStyle } from 'styled-components';

import { Breakpoint, tokens } from '@treams/treams-ui';

import { RightSectionPanel } from './rightSectionContext';

const CRISP_TOP = '--treams-crisp-top';
const CRISP_LEFT = '--treams-crisp-left';
const CRISP_WIDTH = '--treams-crisp-width';
const CRISP_HEIGHT = '--treams-crisp-height';
const DOCK_VISIBLE = `(min-width: calc(${Breakpoint.Medium} + 0.01px))`;

export const CrispDockStyles = createGlobalStyle`
	@media screen and ${DOCK_VISIBLE} {
		body.treams-dock-crisp #crisp-chatbox > div > div:not(:has([role='dialog'])) {
			display: none !important;
		}
	}

	body.treams-crisp-open #crisp-chatbox > div > div:has([role='dialog']) {
		position: fixed !important;
		top: var(${CRISP_TOP}) !important;
		left: var(${CRISP_LEFT}) !important;
		width: var(${CRISP_WIDTH}) !important;
		height: var(${CRISP_HEIGHT}) !important;
		right: auto !important;
		bottom: auto !important;
		max-width: none !important;
		max-height: none !important;
		margin: 0 !important;
		transform: none !important;
		border: 1px solid ${tokens.border.subtle} !important;
		border-radius: ${tokens.radius.xl}px !important;
		box-shadow: none !important;
		overflow: hidden !important;
		background: ${tokens.surface.white} !important;
		z-index: 20 !important;
	}

	body.treams-crisp-open #crisp-chatbox [role='dialog'],
	body.treams-crisp-open #crisp-chatbox [role='dialog'] > div {
		width: 100% !important;
		height: 100% !important;
		max-width: none !important;
		max-height: none !important;
		border-radius: 0 !important;
	}

	body.treams-crisp-open #crisp-chatbox > div > span {
		display: none !important;
	}
`;

function crispPush(command: unknown[]) {
	const crisp = (window as Window & { $crisp?: { push: (entry: unknown[]) => void } }).$crisp;

	if (!crisp) {
		return;
	}

	crisp.push(command);
}

function chatWindow(): HTMLElement | null {
	return document.querySelector("#crisp-chatbox > div > div:has([role='dialog'])");
}

function setImportant(element: HTMLElement, property: string, value: string) {
	if (
		element.style.getPropertyValue(property) === value &&
		element.style.getPropertyPriority(property) === 'important'
	) {
		return;
	}

	element.style.setProperty(property, value, 'important');
}

function placeCrisp(pane: HTMLElement) {
	const frame = pane.closest('aside') ?? pane;
	const rect = frame.getBoundingClientRect();

	if (rect.width === 0 || rect.height === 0) {
		return;
	}

	const width = `${rect.width}px`;
	const height = `${rect.height}px`;
	const top = `${rect.top}px`;
	const left = `${rect.left}px`;
	const { style } = document.body;
	style.setProperty(CRISP_TOP, top);
	style.setProperty(CRISP_LEFT, left);
	style.setProperty(CRISP_WIDTH, width);
	style.setProperty(CRISP_HEIGHT, height);
	document.body.classList.add('treams-crisp-open');

	const windowEl = chatWindow();

	if (!windowEl) {
		return;
	}

	setImportant(windowEl, 'position', 'fixed');
	setImportant(windowEl, 'box-sizing', 'border-box');
	setImportant(windowEl, 'top', top);
	setImportant(windowEl, 'left', left);
	setImportant(windowEl, 'width', width);
	setImportant(windowEl, 'height', height);
	setImportant(windowEl, 'right', 'auto');
	setImportant(windowEl, 'bottom', 'auto');
	setImportant(windowEl, 'max-width', 'none');
	setImportant(windowEl, 'max-height', 'none');
	setImportant(windowEl, 'margin', '0');
	setImportant(windowEl, 'transform', 'none');
	setImportant(windowEl, 'border-radius', `${tokens.radius.xl}px`);
	setImportant(windowEl, 'box-shadow', 'none');
	setImportant(windowEl, 'overflow', 'hidden');
	setImportant(windowEl, 'z-index', '20');

	const dialog = windowEl.querySelector('[role="dialog"]');
	const inner = dialog?.firstElementChild;

	for (const element of [dialog, inner]) {
		if (!(element instanceof HTMLElement)) {
			continue;
		}

		setImportant(element, 'width', '100%');
		setImportant(element, 'height', '100%');
		setImportant(element, 'max-width', 'none');
		setImportant(element, 'max-height', 'none');
	}
}

const WINDOW_PROPS = [
	'position',
	'box-sizing',
	'top',
	'left',
	'width',
	'height',
	'right',
	'bottom',
	'max-width',
	'max-height',
	'margin',
	'transform',
	'border-radius',
	'box-shadow',
	'overflow',
	'z-index',
];

const INNER_PROPS = ['width', 'height', 'max-width', 'max-height'];

function releaseChatWindow() {
	const windowEl = chatWindow();

	if (!windowEl) {
		return;
	}

	for (const property of WINDOW_PROPS) {
		windowEl.style.removeProperty(property);
	}

	const dialog = windowEl.querySelector('[role="dialog"]');
	const inner = dialog?.firstElementChild;

	for (const element of [dialog, inner]) {
		if (!(element instanceof HTMLElement)) {
			continue;
		}

		for (const property of INNER_PROPS) {
			element.style.removeProperty(property);
		}
	}
}

function clearCrispPlacement() {
	document.body.classList.remove('treams-crisp-open');
	document.body.style.removeProperty(CRISP_TOP);
	document.body.style.removeProperty(CRISP_LEFT);
	document.body.style.removeProperty(CRISP_WIDTH);
	document.body.style.removeProperty(CRISP_HEIGHT);
	releaseChatWindow();
}

export function useCrispDock(
	panel: RightSectionPanel,
	setPanel: (
		panel: RightSectionPanel | ((current: RightSectionPanel) => RightSectionPanel),
	) => void,
	helpPane: HTMLElement | null,
	asideOpen: boolean,
) {
	useEffect(() => {
		document.body.classList.add('treams-dock-crisp');
		Crisp.setAvailabilityTooltip(false);

		const openHelp = () => setPanel('help');
		const closeHelp = () => setPanel((current) => (current === 'help' ? null : current));

		Crisp.chat.onChatOpened(openHelp);
		Crisp.chat.onChatClosed(closeHelp);

		return () => {
			Crisp.chat.offChatOpened();
			Crisp.chat.offChatClosed();
			document.body.classList.remove('treams-dock-crisp');
			clearCrispPlacement();
		};
	}, [setPanel]);

	useEffect(() => {
		if (panel === 'help') {
			crispPush(['do', 'chat:open']);
			return;
		}

		if (Crisp.chat.isChatOpened()) {
			crispPush(['do', 'chat:close']);
		}
	}, [panel]);

	useLayoutEffect(() => {
		const wide = window.matchMedia(DOCK_VISIBLE).matches;

		if (panel !== 'help' || !asideOpen || !helpPane || !wide) {
			clearCrispPlacement();
			return;
		}

		const apply = () => placeCrisp(helpPane);
		apply();

		const observer = new ResizeObserver(apply);
		observer.observe(helpPane);
		const column = helpPane.closest('main')?.parentElement;
		if (column) {
			observer.observe(column);
		}

		const crispRoot = document.getElementById('crisp-chatbox');
		const mutations = new MutationObserver(apply);
		if (crispRoot) {
			mutations.observe(crispRoot, {
				attributes: true,
				attributeFilter: ['style'],
				childList: true,
				subtree: true,
			});
		}

		window.addEventListener('resize', apply);

		return () => {
			observer.disconnect();
			mutations.disconnect();
			window.removeEventListener('resize', apply);
			clearCrispPlacement();
		};
	}, [asideOpen, helpPane, panel]);
}
